import {
  SITE_COLOR,
  SITE_EASE,
  SITE_HEADER_OUTER_HEIGHT_PX,
  SITE_MEDIA,
  SITE_SHADOW,
  SITE_VAR,
} from "src/domains/shared/constants/styles/site";
import { truncateNumber } from "src/domains/shared/utils/truncateNumber";
import { SCENE_BACKGROUND, SCENE_CANVAS_FADE } from "./sceneCanvas";

/** 가로 터치 화면에서 장면 높이에 못 쓰는 세로 — 머리말(테두리 포함) + 위아래 틈 24px */
const LANDSCAPE_VIEW_CHROME_PX = SITE_HEADER_OUTER_HEIGHT_PX + 24;
/** 장면 틀 비율(넓은 화면과 같다) — 가로 터치 화면 폭 상한 계산에도 쓴다 */
const VIEW_ASPECT = "380/240";
/** 폭 상한 계산용 비율 문자열(버림 4자리) */
const LANDSCAPE_VIEW_ASPECT = truncateNumber(380 / 240, 4);

/** 장면 구역 스타일 — 시안 mock.css 의 `.lx*`·`.pf*` 값 그대로 */
export const SCENE_STYLES = {
  layout: {
    display: "grid",
    gridTemplateColumns: "minmax(220px,292px) minmax(0,1fr)",
    gap: "clamp(28px,3.6vw,56px)",
    alignItems: "center",
    [SITE_MEDIA.tablet]: { gridTemplateColumns: "minmax(0,1fr)", gap: "12px" },
    // 가로 터치 화면: 세로 쌓기(장면이 눌려 납작해진다) 대신 넓은 화면처럼 단계 목록 | 장면 두 칸 — 장면은 화면 높이에 맞춘 380:240
    [SITE_MEDIA.landscapeTouch]: {
      gridTemplateColumns: "minmax(220px,292px) minmax(0,1fr)",
      gap: "24px",
      alignItems: "start",
      "& [data-scene-steps]": { gridColumn: 1, gridRow: 1 },
      "& [data-scene-view]": { gridColumn: 2, gridRow: "1 / span 2" },
      "& [data-scene-caption]": { gridColumn: 1, gridRow: 2 },
    },
  },
  layoutReverse: {
    gridTemplateColumns: "minmax(0,1fr) minmax(220px,292px)",
    "& [data-scene-steps]": { order: 2 },
    [SITE_MEDIA.tablet]: {
      gridTemplateColumns: "minmax(0,1fr)",
      "& [data-scene-steps]": { order: 0 },
    },
    [SITE_MEDIA.landscapeTouch]: {
      gridTemplateColumns: "minmax(0,1fr) minmax(220px,292px)",
      "& [data-scene-steps]": { gridColumn: 2, gridRow: 1 },
      "& [data-scene-view]": { gridColumn: 1, gridRow: "1 / span 2" },
      "& [data-scene-caption]": { gridColumn: 2, gridRow: 2 },
    },
  },
  steps: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    [SITE_MEDIA.tablet]: {
      order: 0,
      flexDirection: "row",
      gap: "6px",
      overflowX: "auto",
      scrollbarWidth: "none",
      paddingBottom: "2px",
      "&::-webkit-scrollbar": { display: "none" },
    },
    // 가로 터치 화면: 좁은 화면 칩을 한 열로
    [SITE_MEDIA.landscapeTouch]: {
      flexDirection: "column",
      gap: "4px",
      overflowX: "visible",
      overflowY: "auto",
      // 단계 설명 한 줄(약 45px)을 뺀 높이 — 열이 장면 위 끝에 맞춰 시작해 머리말 뒤로 밀리지 않는다
      maxHeight: `calc(100svh - ${LANDSCAPE_VIEW_CHROME_PX}px - 45px)`,
      paddingBottom: 0,
    },
  },
  step: {
    position: "relative",
    display: "grid",
    gridTemplateColumns: "30px minmax(0,1fr)",
    columnGap: "10px",
    textAlign: "left",
    padding: "14px 14px 14px 18px",
    borderRadius: "12px",
    color: SITE_COLOR.ink3,
    transition: "color .25s,background-color .25s",
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: "14px",
      bottom: "14px",
      width: "2px",
      borderRadius: "2px",
      background: SITE_COLOR.line,
    },
    "& [data-bar]": {
      position: "absolute",
      left: 0,
      top: "14px",
      bottom: "14px",
      width: "2px",
      borderRadius: "2px",
      background: SITE_COLOR.blue,
      transform: "scaleY(0)",
      transformOrigin: "top",
      zIndex: 1,
    },
    "& [data-n]": {
      gridRow: "1/3",
      fontSize: "13px",
      fontWeight: 700,
      lineHeight: "24px",
      fontVariantNumeric: "tabular-nums",
    },
    "& [data-k]": { fontSize: "17px", fontWeight: 700, lineHeight: "24px" },
    "& [data-d]": { fontSize: "14px", lineHeight: 1.5, marginTop: "2px" },
    '&[aria-selected="true"]': {
      color: SITE_COLOR.ink,
      background: SITE_COLOR.panel,
    },
    '&[aria-selected="true"] [data-n]': { color: SITE_COLOR.blue },
    '&[aria-selected="true"] [data-d]': { color: SITE_COLOR.ink2 },
    "&:hover": { color: SITE_COLOR.ink2 },
    [SITE_MEDIA.tablet]: {
      flex: "none",
      display: "block",
      padding: "9px 13px 11px",
      borderRadius: "10px",
      background: SITE_COLOR.panel,
      "&::before": { display: "none" },
      "& [data-bar]": {
        left: "10px",
        right: "10px",
        top: "auto",
        bottom: "4px",
        width: "auto",
        height: "2px",
        transform: "scaleX(0)",
        transformOrigin: "left",
      },
      "& [data-n]": { display: "none" },
      "& [data-k]": {
        fontSize: "14px",
        lineHeight: "20px",
        whiteSpace: "nowrap",
      },
      "& [data-d]": { display: "none" },
      '&[aria-selected="true"]': { background: SITE_COLOR.ink, color: "#fff" },
      '&[aria-selected="true"] [data-bar]': { background: "#fff" },
    },
    // 가로 터치 화면: 한 열에 단계 여덟 개(레이어 중첩)까지 화면 높이 안에 들어오게 칩을 조금 낮춘다
    [SITE_MEDIA.landscapeTouch]: { padding: "7px 12px 9px" },
  },
  view: {
    position: "relative",
    width: "100%",
    aspectRatio: VIEW_ASPECT,
    maxHeight: `calc(100svh - ${SITE_HEADER_OUTER_HEIGHT_PX}px - 56px)`,
    borderRadius: SITE_VAR.radiusImage,
    overflow: "hidden",
    background: SCENE_BACKGROUND,
    isolation: "isolate",
    boxShadow: SITE_SHADOW.frame,
    "& > canvas": {
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
      display: "block",
    },
    [SITE_MEDIA.tablet]: {
      order: -1,
      aspectRatio: "1.25",
      maxHeight: `calc(100svh - ${SITE_HEADER_OUTER_HEIGHT_PX}px - 140px)`,
      borderRadius: "16px",
    },
    [SITE_MEDIA.phone]: { aspectRatio: "1.08" },
    // 가로 터치 화면: 넓은 화면 비율로, 높이가 머리말 아래 화면 높이를 넘지 않는 폭까지(비율을 지키며 칸 가운데)
    [SITE_MEDIA.landscapeTouch]: {
      aspectRatio: VIEW_ASPECT,
      maxHeight: "none",
      maxWidth: `calc((100svh - ${LANDSCAPE_VIEW_CHROME_PX}px) * ${LANDSCAPE_VIEW_ASPECT})`,
      justifySelf: "center",
      borderRadius: "16px",
    },
  },
  viewBridge: {
    [SITE_MEDIA.tablet]: { aspectRatio: ".95" },
    [SITE_MEDIA.phone]: { aspectRatio: ".95" },
    [SITE_MEDIA.landscapeTouch]: { aspectRatio: VIEW_ASPECT },
    // 내보내기: 장면과 그래프는 옅게 어둡게, 팝업만 위로 올려 강조
    "&[data-export] [data-dim]": { opacity: 1 },
    "&[data-export] [data-panel-box]": { zIndex: 5 },
    "&[data-export] [data-panel-box]::after": { opacity: 1 },
    "&[data-export] [data-pop]": {
      zIndex: 2,
      boxShadow:
        "0 18px 48px rgba(10,20,40,.38),0 0 0 2px rgba(29,116,255,.55)",
    },
  },
  caption: {
    display: "none",
    [SITE_MEDIA.tablet]: {
      display: "block",
      order: 1,
      margin: 0,
      fontSize: "15px",
      lineHeight: 1.5,
      color: SITE_COLOR.ink2,
      minHeight: "1.5em",
    },
    [SITE_MEDIA.landscapeTouch]: { alignSelf: "start", fontSize: "14px" },
  },
  fallback: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transition: SCENE_CANVAS_FADE,
  },
  hud: {
    position: "absolute",
    left: "14px",
    top: "14px",
    display: "flex",
    pointerEvents: "none",
    zIndex: 3,
    [SITE_MEDIA.phone]: { left: "10px", top: "10px" },
  },
  mode: {
    display: "inline-flex",
    padding: "3px",
    borderRadius: "9px",
    background: "rgba(255,255,255,.94)",
    boxShadow: "0 2px 10px rgba(20,30,45,.14)",
    "& b": {
      fontSize: "13px",
      fontWeight: 700,
      lineHeight: 1,
      padding: "7px 10px",
      borderRadius: "6px",
      color: SITE_COLOR.ink3,
      transition: "background-color .3s,color .3s",
    },
    "& b[data-on]": { background: SITE_COLOR.blue, color: "#fff" },
    [SITE_MEDIA.phone]: { "& b": { padding: "6px 8px", fontSize: "12px" } },
  },
  /** 실제 앱 날짜 선택(DateSelector·ListSelect 220px) 모양 */
  date: {
    position: "absolute",
    zIndex: 2,
    display: "flex",
    alignItems: "center",
    gap: "10px",
    width: "220px",
    maxWidth: "calc(100% - 16px)",
    height: "36px",
    padding: "0 8px 0 12px",
    borderRadius: "6px",
    background: "#fff",
    boxShadow: "0 2px 8px rgba(20,30,45,.16)",
    fontSize: "14px",
    color: "rgba(0,0,0,.87)",
    fontVariantNumeric: "tabular-nums",
    pointerEvents: "none",
    transition: "opacity .35s ease",
    "& [data-text]": {
      flex: 1,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    "& [data-divider]": {
      flex: "none",
      width: "1px",
      height: "20px",
      background: "rgba(0,0,0,.12)",
    },
    "& svg": { flex: "none", color: "rgba(0,0,0,.54)" },
  },
  mainDate: {
    right: "12px",
    bottom: "12px",
    "&[data-off]": { opacity: 0 },
    [SITE_MEDIA.phone]: {
      right: "10px",
      bottom: "10px",
      width: "auto",
      height: "30px",
      fontSize: "12px",
      gap: "6px",
      padding: "0 6px 0 10px",
    },
  },
  paneDate: {
    left: 0,
    top: 0,
    opacity: 0,
    width: "auto",
    height: "32px",
    fontSize: "13px",
    gap: "8px",
    padding: "0 6px 0 10px",
    transform: "translate(-100%,-100%)",
    "&[data-on]": { opacity: 1 },
    [SITE_MEDIA.phone]: {
      height: "24px",
      fontSize: "10.5px",
      gap: "4px",
      padding: "0 4px 0 7px",
      "& [data-divider], & svg": { display: "none" },
    },
  },
  /** 현장 기록 단계: 실제 앱 영상 기록 미리보기 카드(캡처) — 위치(--gx/--gy)는 장면이 마커 자리로 준다 */
  geologCard: {
    position: "absolute",
    left: 0,
    top: 0,
    zIndex: 2,
    width: "clamp(130px,23%,190px)",
    height: "auto",
    maxWidth: "none",
    pointerEvents: "none",
    opacity: 0,
    transformOrigin: "50% 100%",
    transform:
      "translate(var(--gx,50%),var(--gy,50%)) translate(-50%,-100%) scale(.86)",
    transition: `opacity .22s ease,transform .3s ${SITE_EASE.out}`,
    filter: "drop-shadow(0 6px 14px rgba(0,0,0,.28))",
    "&[data-on]": {
      opacity: 1,
      transform:
        "translate(var(--gx,50%),var(--gy,50%)) translate(-50%,-100%) scale(1)",
    },
  },
  workSite: {
    position: "absolute",
    inset: 0,
    zIndex: 4,
    opacity: 0,
    transition: "opacity .7s ease",
    background: "#1f2329",
    pointerEvents: "none",
    "&[data-on]": { opacity: 1 },
    "& video": {
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
      objectFit: "cover",
      objectPosition: "50% 50%",
      display: "block",
    },
  },
} as const;
